import type { FC } from 'react';
import React, { useCallback } from 'react';
import cn from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { ButtonDesktop as Button } from '@alfa-bank/site-button-v2/desktop';
import { InvestPassportSN } from '@alfa-bank/site-invest-passport-sn';
import { FormDesktop as Form } from '@alfa-bank/site-form/desktop';
import { TextDesktop as Text } from '@alfa-bank/site-text-v2/desktop';
import { Aligner } from '@alfa-bank/site-aligner';
import { PlateDesktop as Plate } from '@alfa-bank/site-plate/desktop';
import { Img } from '@alfa-bank/site-img';
import type { TFormValues } from '@alfa-bank/site-form/src/models';
import type { FormApi } from 'final-form';
import { FormProgressBarV2Desktop } from '@alfa-bank/site-form-progress-bar-v2/desktop';

import type { IPassportAdditionFormValues } from '../../store';
import {
    selectShowProgressBar,
    selectPassportAddition,
    submitPassportAddition,
    setFormFieldsPassportAddition,
} from '../../store';
import { AnalyticsSpy } from '../../components/analytics-spy';
import type { TFormSubmitCallback } from '../../models/common-types';

import { defaultPassportAdditionSettingsDesktop } from './config/default-values';
import type { TPassportAdditionStepDesktop } from './model';
import styles from './styles/desktop.css';
import { analyticsPassportAdditionSmev } from './analytics';

export const PassportAdditionStepDesktop: FC<TPassportAdditionStepDesktop> = ({
    passportSettings = defaultPassportAdditionSettingsDesktop.passportSettings,
    progressBarSettings = defaultPassportAdditionSettingsDesktop.progressBarSettings,
    plateButtonText = defaultPassportAdditionSettingsDesktop.plateButtonText,
    plateDescription = defaultPassportAdditionSettingsDesktop.plateDescription,
    plateImageSrc,
    title,
}) => {
    const showProgressBar = useSelector(selectShowProgressBar);
    const state = useSelector(selectPassportAddition);
    const dispatch = useDispatch();

    const handleOnSubmit = useCallback(
        (
            _: TFormValues,
            formApi: FormApi<TFormValues>,
            finishSubmitCallback: TFormSubmitCallback
        ) => {
            const fields = formApi.getState().values as unknown as IPassportAdditionFormValues;

            dispatch(setFormFieldsPassportAddition(fields));
            dispatch(submitPassportAddition());

            finishSubmitCallback();
        },
        [dispatch]
    );

    return (
        <Form
            analytics={{
                submit: analyticsPassportAdditionSmev.formSubmitSuccess,
                submitOnFail: analyticsPassportAdditionSmev.formSubmitFail,
            }}
            initialValues={{
                ...state.formState,
            }}
            onSubmit={handleOnSubmit}
            view="without-shadow"
        >
            <AnalyticsSpy analytics={analyticsPassportAdditionSmev} />
            {title && (
                <Aligner styler={{ marginBottom: 24 }}>
                    <Text styler={{ marginTop: 0, marginBottom: 0 }} tag="h2" view="medium">
                        {title}
                    </Text>
                </Aligner>
            )}
            {showProgressBar && (
                <FormProgressBarV2Desktop
                    caption={progressBarSettings.caption}
                    currentStepName={progressBarSettings.currentStepName}
                    hatchValue={progressBarSettings.hatchValue}
                    header={progressBarSettings.header}
                    line={progressBarSettings.line}
                    maxStep={progressBarSettings.maxStep}
                    nextStepName={progressBarSettings.nextStepName}
                    steps={progressBarSettings.steps}
                    styler={{ marginBottom: 24 }}
                    value={progressBarSettings.value}
                />
            )}
            <InvestPassportSN
                {...passportSettings}
                disabled={state.isLoading}
                styler={{ marginBottom: 24 }}
            />
            <Plate
                // TODO нужно поменять когда будет дизайн токен известен
                background={'#F0EFFF'}
                border={false}
                leftAddons={<Img height={32} src={plateImageSrc} width={32}></Img>}
                shadow={false}
                subAddons={
                    <Button loading={state.isLoading} size="s" type="submit" view="primary">
                        {plateButtonText}
                    </Button>
                }
                subAddonsClassName={cn(styles.plateSubAddons, styles.external)}
                view="custom"
            >
                <Text styler={{ marginTop: 0, marginBottom: 8 }} view="primary-small">
                    {plateDescription}
                </Text>
            </Plate>
        </Form>
    );
};
